import React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";
import { Delete, Edit, ForwardToInbox } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { BORDER_RADIUS } from "../../theme/theme";
import { EMAIL_REGEX, PHONE_REGEX, VAL_MSG } from "../utils/validation";

export default function OrgAdminUsersPanel(props: any) {
  const {
    users,
    searchUserText,
    setSearchUserText,
    openUserRow,
    setOpenUserRow,
    organisationList,
    login,
    handleOpenModal,
    handleExpandUser,
    handleUserInputChange,
    handleSaveUser,
    handleResendInvite,
    handleDeleteUser,
    passwordModalOpen,
    setPasswordModalOpen,
    passwordModalValues,
    setPasswordModalValues,
    passwordModalError,
    setPasswordModalError,
    setPasswordModalUserId,
    handleSetPassword,
  } = props;

  return (
    <Box
      id="users-tab"
      sx={{ mt: 2, p: 3, bgcolor: "background.paper", borderRadius: BORDER_RADIUS }}
    >
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal()}
        >
          Create user
        </Button>
      </Box>

      <TextField
        label="Search by User Name, Phone Number or Email"
        variant="outlined"
        fullWidth
        value={searchUserText}
        onChange={(e) => setSearchUserText(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: "20px" }} />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>User Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Phone Number</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Role</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Organisation</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>2FA</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users
              .filter((user: any) => user.orgId === login?.data?.orgId)
              .map((user: any) => {
                const index = users.findIndex((u: any) => u._id === user._id);
                return (
                  <React.Fragment key={user._id}>
                    <TableRow>
                      <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role || "-"}</TableCell>
                      <TableCell>
                        {organisationList.find(
                          (org: any) => org._id === user.orgId,
                        )?.name || "-"}
                      </TableCell>
                      <TableCell>{user.status || "-"}</TableCell>
                      <TableCell>
                        <Switch
                          checked={user.twoFactorEnabled}
                          disabled
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit User" arrow placement="top">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleExpandUser(index)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {user.status === "invited" && (
                          <Tooltip title="Resend Invite" arrow placement="top">
                            <IconButton
                              color="warning"
                              size="small"
                              onClick={() => handleResendInvite(user._id)}
                            >
                              <ForwardToInbox />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete User" arrow placement="top">
                          <IconButton
                            onClick={() => handleDeleteUser(user._id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {openUserRow === index && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0 }}>
                          <Collapse in timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: "#f8f8f8" }}>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(3, 1fr)",
                                  gap: 2,
                                  alignItems: "center",
                                }}
                              >
                                {/* First Name */}
                                <TextField
                                  label="First Name"
                                  value={user.firstName}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "firstName",
                                      e.target.value,
                                    )
                                  }
                                  error={
                                    user.firstName
                                      ? !/[a-zA-Z]/.test(user.firstName)
                                      : false
                                  }
                                  helperText={
                                    user.firstName &&
                                    !/[a-zA-Z]/.test(user.firstName)
                                      ? "First name cannot be purely numerical"
                                      : ""
                                  }
                                />

                                {/* Last Name */}
                                <TextField
                                  label="Last Name"
                                  value={user.lastName}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "lastName",
                                      e.target.value,
                                    )
                                  }
                                  error={
                                    user.lastName
                                      ? !/[a-zA-Z]/.test(user.lastName)
                                      : false
                                  }
                                  helperText={
                                    user.lastName &&
                                    !/[a-zA-Z]/.test(user.lastName)
                                      ? "Last name cannot be purely numerical"
                                      : ""
                                  }
                                />

                                {/* Phone */}
                                <TextField
                                  label="Phone"
                                  value={user.phone}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "phone",
                                      e.target.value,
                                    )
                                  }
                                  error={
                                    user.phone
                                      ? !PHONE_REGEX.test(user.phone)
                                      : false
                                  }
                                  helperText={
                                    user.phone &&
                                    !PHONE_REGEX.test(user.phone)
                                      ? "Enter a valid phone number (e.g. +61412345678 or 0412345678)"
                                      : ""
                                  }
                                />

                                {/* Mobile */}
                                <TextField
                                  label="Mobile"
                                  value={user.mobile}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "mobile",
                                      e.target.value,
                                    )
                                  }
                                  error={
                                    user.mobile
                                      ? !PHONE_REGEX.test(user.mobile)
                                      : false
                                  }
                                  helperText={
                                    user.mobile &&
                                    !PHONE_REGEX.test(user.mobile)
                                      ? "Enter a valid mobile number (e.g. +61412345678 or 0412345678)"
                                      : ""
                                  }
                                />

                                {/* Email */}
                                <TextField
                                  label="Email"
                                  value={user.email}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "email",
                                      e.target.value,
                                    )
                                  }
                                  error={
                                    user.email
                                      ? !EMAIL_REGEX.test(user.email)
                                      : false
                                  }
                                  helperText={
                                    user.email &&
                                    !EMAIL_REGEX.test(user.email)
                                      ? VAL_MSG.email
                                      : ""
                                  }
                                />

                                <TextField
                                  label="Title"
                                  value={user.title}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "title",
                                      e.target.value,
                                    )
                                  }
                                />

                                {/* Role Dropdown */}
                                <TextField
                                  select
                                  label="Role"
                                  value={user.role || "user"}
                                  onChange={(e) =>
                                    handleUserInputChange(
                                      user._id,
                                      "role",
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                  disabled={
                                    login?.data?.role === "orgadmin" &&
                                    user.role === "global admin"
                                  }
                                >
                                  <MenuItem value="user">User</MenuItem>
                                  <MenuItem value="orgadmin">
                                    Organisation Admin
                                  </MenuItem>
                                  {(login?.data?.role === "global admin" ||
                                    user.role === "global admin") && (
                                    <MenuItem value="global admin">
                                      Global Admin
                                    </MenuItem>
                                  )}
                                </TextField>

                                {/* Status */}
                                <TextField
                                  select
                                  label="Status"
                                  value={user.status || "active"}
                                  onChange={(e) =>
                                    user.status !== "invited" &&
                                    handleUserInputChange(
                                      user._id,
                                      "status",
                                      e.target.value,
                                    )
                                  }
                                  fullWidth
                                >
                                  {user.status === "invited" && (
                                    <MenuItem value="invited" disabled>
                                      Invited
                                    </MenuItem>
                                  )}
                                  {user.status === "invited" && (
                                    <MenuItem
                                      disabled
                                      sx={{
                                        fontSize: 12,
                                        color: "text.secondary",
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      Status will automatically change to
                                      Active once the user accepts the invite.
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    value="active"
                                    disabled={user.status === "invited"}
                                  >
                                    Active
                                  </MenuItem>
                                  <MenuItem
                                    value="inactive"
                                    disabled={user.status === "invited"}
                                  >
                                    Inactive
                                  </MenuItem>
                                </TextField>

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={user.twoFactorEnabled}
                                      onChange={(e) =>
                                        handleUserInputChange(
                                          user._id,
                                          "twoFactorEnabled",
                                          e.target.checked,
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label="Two-Factor Authentication"
                                />
                              </Box>

                              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                                <Button
                                  variant="contained"
                                  onClick={() => handleSaveUser(user._id)}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() => setOpenUserRow(null)}
                                >
                                  Cancel
                                </Button>
                                {(login?.data?.role === "global admin" ||
                                  (login?.data?.role === "orgadmin" &&
                                    user.role !== "global admin")) && (
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                      setPasswordModalUserId(user._id);
                                      setPasswordModalOpen(true);
                                    }}
                                  >
                                    Change Password
                                  </Button>
                                )}
                              </Box>

                              {/* Password Change Modal */}
                              <Dialog
                                open={passwordModalOpen}
                                onClose={() => setPasswordModalOpen(false)}
                                maxWidth="xs"
                                fullWidth
                              >
                                <DialogTitle>Set New Password</DialogTitle>
                                <DialogContent>
                                  <TextField
                                    autoFocus
                                    margin="dense"
                                    label="New Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={passwordModalValues.password}
                                    onChange={(e) =>
                                      setPasswordModalValues((v: any) => ({
                                        ...v,
                                        password: e.target.value,
                                      }))
                                    }
                                    helperText="Min 8 chars, uppercase, lowercase, number & special char (@$!%*?&)"
                                  />
                                  <TextField
                                    margin="dense"
                                    label="Confirm New Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={passwordModalValues.confirm}
                                    onChange={(e) =>
                                      setPasswordModalValues((v: any) => ({
                                        ...v,
                                        confirm: e.target.value,
                                      }))
                                    }
                                    error={
                                      passwordModalValues.password !==
                                        passwordModalValues.confirm &&
                                      passwordModalValues.confirm !== ""
                                    }
                                    helperText={
                                      passwordModalValues.password !==
                                        passwordModalValues.confirm &&
                                      passwordModalValues.confirm !== ""
                                        ? "Passwords do not match"
                                        : ""
                                    }
                                  />
                                  {passwordModalError && (
                                    <Typography
                                      color="error"
                                      variant="body2"
                                      sx={{ mt: 1 }}
                                    >
                                      {passwordModalError}
                                    </Typography>
                                  )}
                                </DialogContent>
                                <DialogActions>
                                  <Button
                                    onClick={() => {
                                      setPasswordModalOpen(false);
                                      setPasswordModalError("");
                                      setPasswordModalUserId(null);
                                      setPasswordModalValues({
                                        password: "",
                                        confirm: "",
                                      });
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    onClick={handleSetPassword}
                                  >
                                    Set Password
                                  </Button>
                                </DialogActions>
                              </Dialog>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
